import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRealtimeChannel } from "@workspace/database/realtime-client";
import type { GetConversationsResponseDto } from "@workspace/dto/conversation/get-conversations";
import type { NotificationDto } from "@workspace/dto/notification/schema";
import {
  renderDescription,
  renderTitle,
} from "@workspace/notification-service/handlers";
import type { AxiosResponse } from "axios";
import { usePathname } from "next/navigation";
import { useRouter } from "next/router";
import {
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { toast } from "sonner";
import type { ExtendedUser } from "@/auth";
import { useStableSession } from "@/lib/auth/use-stable-session";
import { axios } from "@/lib/axios";
import { useLatestValue } from "@/lib/hooks/use-latest-value";
import { shouldEnableNotifications } from "@/lib/notifications/notification-availability";
import {
  type ConversationEndedPayload,
  shouldAcceptRealtimeConversationEvent,
  shouldAcceptRealtimeNotification,
} from "@/lib/notifications/realtime-scope";
import { toastMutationNetworkError } from "@/lib/query-failure";
import { queryKeys } from "@/lib/query-keys";

const UNREAD_TITLE_PREFIX = /^\(\d+\)\s*/;
const EMPTY_NOTIFICATIONS: NotificationDto[] = [];
const EMPTY_CONVERSATION_IDS: string[] = [];

type NotificationContextType = {
  unreadConversationIds: string[];
  clearUnread: (id: string, force?: boolean) => void;
  notifications: NotificationDto[];
  archivedNotifications: NotificationDto[];
  hasUnreadNotifications: boolean;
  isNotificationsLoading: boolean;
  markNotificationAsRead: (id: string) => void;
  archiveNotification: (id: string) => void;
  archiveAllNotifications: () => void;
  markAllNotificationsAsRead: () => void;
};

const NotificationContext = createContext<NotificationContextType>({
  unreadConversationIds: [],
  clearUnread: () => {
    // Initialized by provider.
  },
  notifications: [],
  archivedNotifications: [],
  hasUnreadNotifications: false,
  isNotificationsLoading: false,
  markNotificationAsRead: () => {
    // Initialized by provider.
  },
  archiveNotification: () => {
    // Initialized by provider.
  },
  archiveAllNotifications: () => {
    // Initialized by provider.
  },
  markAllNotificationsAsRead: () => {
    // Initialized by provider.
  },
});

function updateConversationUnreadCache({
  conversationId,
  content,
  queryClient,
  senderId,
  userId,
  isCurrentlyInChat,
}: {
  conversationId: string;
  content: string;
  queryClient: ReturnType<typeof useQueryClient>;
  senderId?: string;
  userId?: string;
  isCurrentlyInChat: boolean;
}) {
  queryClient.setQueryData(
    queryKeys.conversations.all,
    (oldData: GetConversationsResponseDto | undefined) => {
      if (!oldData) {
        return oldData;
      }

      return {
        ...oldData,
        conversations: oldData.conversations.map((conversation) => {
          if (conversation.id !== conversationId) {
            return conversation;
          }
          return {
            ...conversation,
            lastMessage: {
              content,
              from: senderId ?? "",
              createdAt: new Date().toISOString(),
            },
            unreadBy:
              !isCurrentlyInChat &&
              userId &&
              !conversation.unreadBy.includes(userId)
                ? [...conversation.unreadBy, userId]
                : conversation.unreadBy,
          };
        }),
      };
    }
  );
}

function useDocumentNotificationTitle(count: number) {
  const baseTitleRef = useRef<string | null>(null);

  useEffect(() => {
    if (typeof document === "undefined") {
      return;
    }
    if (baseTitleRef.current === null) {
      baseTitleRef.current = document.title.replace(UNREAD_TITLE_PREFIX, "");
    }
    const base = baseTitleRef.current;
    document.title = count > 0 ? `(${count}) ${base}` : base;
  }, [count]);
}

type NotificationListData = {
  notifications: NotificationDto[];
  nextCursor: string | null;
};

function getNotificationMetadataString(
  notification: NotificationDto,
  key: string
) {
  const value = notification.metadata[key];
  return typeof value === "string" ? value : undefined;
}

function addNotificationToInboxCache({
  notification,
  queryClient,
}: {
  notification: NotificationDto;
  queryClient: ReturnType<typeof useQueryClient>;
}) {
  queryClient.setQueryData(
    queryKeys.notifications.inbox,
    (oldData: NotificationListData | undefined) => {
      if (!oldData) {
        return { notifications: [notification], nextCursor: null };
      }
      if (oldData.notifications.some((item) => item.id === notification.id)) {
        return oldData;
      }
      return {
        ...oldData,
        notifications: [notification, ...oldData.notifications],
      };
    }
  );
}

function notificationTargetsCurrentPath({
  currentPath,
  notification,
}: {
  currentPath: string;
  notification: NotificationDto;
}) {
  const href = notification.links?.href;
  return href ? currentPath === href.split("#")[0] : false;
}

function syncMessageNotificationConversation({
  currentPath,
  notification,
  queryClient,
  userId,
}: {
  currentPath: string;
  notification: NotificationDto;
  queryClient: ReturnType<typeof useQueryClient>;
  userId?: string;
}) {
  if (notification.type !== "message.new") {
    return;
  }

  const conversationId = getNotificationMetadataString(
    notification,
    "conversationId"
  );
  if (!conversationId) {
    return;
  }

  updateConversationUnreadCache({
    conversationId,
    content:
      getNotificationMetadataString(notification, "content") ??
      renderDescription(notification.type, notification.metadata),
    queryClient,
    senderId: getNotificationMetadataString(notification, "senderId"),
    userId,
    isCurrentlyInChat: currentPath.includes(conversationId),
  });
}

function showNotificationToast({
  isOnTarget,
  notification,
  router,
}: {
  isOnTarget: boolean;
  notification: NotificationDto;
  router: ReturnType<typeof useRouter>;
}) {
  if (isOnTarget) {
    return;
  }

  const link = notification.links;
  toast(renderTitle(notification.type, notification.metadata), {
    description: renderDescription(notification.type, notification.metadata),
    action: link?.href
      ? {
          label: link.label ?? "View",
          onClick: () => router.push(link.href),
        }
      : undefined,
  });
}

function useUserNotificationChannel({
  enabled,
  organizationId,
  pathname,
  queryClient,
  router,
  userId,
  userType,
}: {
  enabled: boolean;
  organizationId?: string;
  pathname: string;
  queryClient: ReturnType<typeof useQueryClient>;
  router: ReturnType<typeof useRouter>;
  userId?: string;
  userType?: ExtendedUser["type"];
}) {
  const organizationIdRef = useLatestValue(organizationId);
  const pathnameRef = useLatestValue(pathname);
  const queryClientRef = useLatestValue(queryClient);
  const routerRef = useLatestValue(router);
  const userIdRef = useLatestValue(userId);
  const userTypeRef = useLatestValue(userType);

  useRealtimeChannel(enabled && userId ? `user-${userId}` : null, {
    "notification.created": (notification: NotificationDto) => {
      if (
        !shouldAcceptRealtimeNotification({
          notification,
          organizationId: organizationIdRef.current,
          userType: userTypeRef.current,
        })
      ) {
        return;
      }

      const client = queryClientRef.current;
      const currentPath = pathnameRef.current || "";

      addNotificationToInboxCache({ notification, queryClient: client });
      syncMessageNotificationConversation({
        currentPath,
        notification,
        queryClient: client,
        userId: userIdRef.current,
      });
      showNotificationToast({
        isOnTarget: notificationTargetsCurrentPath({
          currentPath,
          notification,
        }),
        notification,
        router: routerRef.current,
      });
    },
    "conversation-ended": (payload: ConversationEndedPayload) => {
      if (
        !shouldAcceptRealtimeConversationEvent({
          organizationId: organizationIdRef.current,
          payload,
          userType: userTypeRef.current,
        })
      ) {
        return;
      }

      queryClientRef.current.setQueryData(
        queryKeys.conversations.all,
        (oldData: GetConversationsResponseDto | undefined) => {
          if (!oldData) {
            return oldData;
          }
          return {
            ...oldData,
            conversations: oldData.conversations.map((conversation) => {
              if (conversation.id !== payload.conversationId) {
                return conversation;
              }
              return {
                ...conversation,
                closed: true,
                unreadBy: userIdRef.current
                  ? conversation.unreadBy.filter(
                      (id) => id !== userIdRef.current
                    )
                  : conversation.unreadBy,
              };
            }),
          };
        }
      );
    },
  });
}

export const NotificationProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { data: session } = useStableSession();
  const pathname = usePathname();
  const router = useRouter();
  const queryClient = useQueryClient();

  const user = session?.user as ExtendedUser | undefined;
  const notificationsEnabled = shouldEnableNotifications({
    pathname,
    userType: user?.type,
  });

  const clearUnread = useCallback(
    async (conversationId: string, force = false) => {
      if (!notificationsEnabled) {
        return;
      }

      let wasUnread = false;
      if (user?.id) {
        queryClient.setQueryData(
          queryKeys.conversations.all,
          (oldData: GetConversationsResponseDto | undefined) => {
            if (!oldData) {
              return oldData;
            }
            return {
              ...oldData,
              conversations: oldData.conversations.map((conversation) => {
                if (conversation.id !== conversationId) {
                  return conversation;
                }
                if (conversation.unreadBy.includes(user.id)) {
                  wasUnread = true;
                  return {
                    ...conversation,
                    unreadBy: conversation.unreadBy.filter(
                      (id) => id !== user.id
                    ),
                  };
                }
                return conversation;
              }),
            };
          }
        );
      }

      if (!(wasUnread || force)) {
        return;
      }

      try {
        await axios.post(`/conversations/${conversationId}/read-message`);
        queryClient.invalidateQueries({
          queryKey: queryKeys.conversations.all,
        });
      } catch (error) {
        if (toastMutationNetworkError(error, "Couldn't mark message as read")) {
          return;
        }
        toast.error("Something went wrong while reading messages");
      }
    },
    [notificationsEnabled, queryClient, user?.id]
  );

  const { data: conversationsData } = useQuery({
    queryKey: queryKeys.conversations.all,
    queryFn: async () => {
      const res = await axios.get<
        GetConversationsResponseDto,
        AxiosResponse<GetConversationsResponseDto>
      >("/conversations/get-conversations");
      return res.data;
    },
    enabled: notificationsEnabled,
  });

  const { data: notificationsData, isLoading: inboxLoading } = useQuery({
    queryKey: queryKeys.notifications.inbox,
    queryFn: async () => {
      const res = await axios.get<
        { notifications: NotificationDto[]; nextCursor: string | null },
        AxiosResponse<{
          notifications: NotificationDto[];
          nextCursor: string | null;
        }>
      >("/notifications/get-notifications");
      return res.data;
    },
    enabled: notificationsEnabled,
  });

  const { data: archivedData, isLoading: archiveLoading } = useQuery({
    queryKey: queryKeys.notifications.archive,
    queryFn: async () => {
      const res = await axios.get<
        { notifications: NotificationDto[]; nextCursor: string | null },
        AxiosResponse<{
          notifications: NotificationDto[];
          nextCursor: string | null;
        }>
      >("/notifications/get-notifications?status=archived");
      return res.data;
    },
    enabled: notificationsEnabled,
  });

  const updateNotificationMutation = useMutation({
    mutationFn: async ({
      id,
      status,
    }: {
      id: string;
      status: "unread" | "read" | "archived";
    }) => {
      if (!notificationsEnabled) {
        return null;
      }

      const response = await axios.patch(`/notifications/${id}/update-status`, {
        status,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.notifications.inbox,
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.notifications.archive,
      });
    },
    onError: (error) => {
      if (toastMutationNetworkError(error, "Couldn't update notification")) {
        return;
      }
      toast.error("Failed to update notification");
    },
  });

  const updateAllNotificationsMutation = useMutation({
    mutationFn: async (status: "unread" | "read" | "archived") => {
      if (!notificationsEnabled) {
        return null;
      }

      const response = await axios.post("/notifications/update-all-status", {
        status,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.notifications.inbox,
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.notifications.archive,
      });
    },
    onError: (error) => {
      if (toastMutationNetworkError(error, "Couldn't update notifications")) {
        return;
      }
      toast.error("Failed to update notifications");
    },
  });

  const notifications = notificationsEnabled
    ? (notificationsData?.notifications ?? EMPTY_NOTIFICATIONS)
    : EMPTY_NOTIFICATIONS;
  const archivedNotifications = notificationsEnabled
    ? (archivedData?.notifications ?? EMPTY_NOTIFICATIONS)
    : EMPTY_NOTIFICATIONS;

  const unreadConversationIds = useMemo(() => {
    if (
      !(notificationsEnabled && conversationsData?.conversations && user?.id)
    ) {
      return EMPTY_CONVERSATION_IDS;
    }

    const ids: string[] = [];
    for (const conversation of conversationsData.conversations) {
      if (conversation.unreadBy.some((id) => id === user.id)) {
        ids.push(conversation.id);
      }
    }
    return ids;
  }, [conversationsData, notificationsEnabled, user?.id]);

  const unreadNotificationCount = useMemo(
    () =>
      notifications.reduce(
        (count, notification) =>
          notification.status === "unread" ? count + 1 : count,
        0
      ),
    [notifications]
  );
  const titleUnreadCount = Math.max(
    unreadNotificationCount,
    unreadConversationIds.length
  );

  useDocumentNotificationTitle(titleUnreadCount);

  useUserNotificationChannel({
    enabled: notificationsEnabled,
    organizationId: user?.organization,
    pathname: pathname ?? "",
    queryClient,
    router,
    userId: user?.id,
    userType: user?.type,
  });

  const { mutate: updateNotification } = updateNotificationMutation;
  const { mutate: updateAllNotifications } = updateAllNotificationsMutation;

  const markNotificationAsRead = useCallback(
    (id: string) => {
      if (notificationsEnabled) {
        updateNotification({ id, status: "read" });
      }
    },
    [notificationsEnabled, updateNotification]
  );
  const archiveNotification = useCallback(
    (id: string) => {
      if (notificationsEnabled) {
        updateNotification({ id, status: "archived" });
      }
    },
    [notificationsEnabled, updateNotification]
  );
  const archiveAllNotifications = useCallback(() => {
    if (notificationsEnabled) {
      updateAllNotifications("archived");
    }
  }, [notificationsEnabled, updateAllNotifications]);
  const markAllNotificationsAsRead = useCallback(() => {
    if (notificationsEnabled) {
      updateAllNotifications("read");
    }
  }, [notificationsEnabled, updateAllNotifications]);

  const value = useMemo(
    () => ({
      unreadConversationIds,
      clearUnread,
      notifications,
      archivedNotifications,
      hasUnreadNotifications: unreadNotificationCount > 0,
      isNotificationsLoading:
        notificationsEnabled && (inboxLoading || archiveLoading),
      markNotificationAsRead,
      archiveNotification,
      archiveAllNotifications,
      markAllNotificationsAsRead,
    }),
    [
      archivedNotifications,
      archiveAllNotifications,
      archiveNotification,
      archiveLoading,
      clearUnread,
      inboxLoading,
      markAllNotificationsAsRead,
      markNotificationAsRead,
      notifications,
      notificationsEnabled,
      unreadConversationIds,
      unreadNotificationCount,
    ]
  );

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => use(NotificationContext);
